import { gsap } from "gsap";

import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Observer } from "gsap/Observer";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { TextPlugin } from "gsap/TextPlugin";

/* The following plugins are Club GSAP perks */
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrambleTextPlugin } from "gsap/ScrambleTextPlugin";
import { SplitText } from "gsap/SplitText";


import { State, Helper } from "infrontjs";

import { HeroWebGl } from "../core/HeroWebGl.js";
import { AboutUsWebGl } from "../core/AboutUsWebGl.js";
import { MainMenuWebGl } from "../core/MainMenuWebGl.js";
import SuperMarquee from "sp-supermarquee";

import template from "./IndexTemplate.html";
import templateOffcanvasMenu from "./OffcanvasMenuTemplate.html";

import refTemplateContainer from "./ref/ReferenceContainerTemplate.html";
import refTemplateActiboard from "./ref/ActiboardTemplate.html";
import refTemplateHighPerformanceComponents from "./ref/HighPerformanceComponentsTemplate.html";
import refTemplateJubelDeal from "./ref/JubelDealTemplate.html";
import refTemplateSuperMarquee from "./ref/SuperMarqueeTemplate.html";
import refTemplateEnportalConnect from "./ref/EnportalConnectTemplate.html";
import refTemplateEnportalCompete from "./ref/EnportalCompeteTemplate.html";
import refTemplateDiversitizeApp from "./ref/DiviersitizeTemplate.html";
import refTemplateYoutine from "./ref/YoutineTemplate.html";

import servTemplateContainer from "./serv/ServiceContainerTemplate.html";
import servPluginApiDevelopment from "./serv/PluginApiDevelopmentTemplate.html";
import servDigitalBusiness from "./serv/DigitalBusinessTemplate.html";
import servGamesCreative from "./serv/GamesCreativeTemplate.html";
import servToolsAutomation from "./serv/ToolsAutomationTemplate.html";

import legalTemplateImprint from "./legal/Imprint.html";
import legalTemplateTerms from "./legal/Terms.html";
import legalTemplateDisclaimer from "./legal/Disclaimer.html";
import legalTemplateDataPrivacy from "./legal/DataPrivacy.html";
import * as IF from "infrontjs";

export class IndexState extends State
{
    static ROUTE = [ '/', '/:refType/:refId' ];
    static ID = 'index';

    async enter()
    {
        this.pageLoader = {
            current : 0,
            total : 6,
            maxHeight : 50,
            init : () => {
                this.pageLoader.maxHeight = parseFloat( window.getComputedStyle( document.querySelector( 'h1.hero' ) ).height ) * 0.8;
                this.pageLoader.cursorLoaderElem = document.querySelector( '.progress-cursor' );
                this.pageLoader.cursorLoaderElem.style.height = '0px';
                this.scrollSmooth.scrollTop( 0 );
            },
            onComplete : () => {
                // HeroTelex
                setTimeout( () =>
                {
                    this.initHeroTelex();
                }, 1000 );
            },
            onProgress : () =>{
                this.pageLoader.current++;
                const newHeight = this.pageLoader.current / this.pageLoader.total * this.pageLoader.maxHeight;
                this.pageLoader.cursorLoaderElem.style.height = newHeight + 'px';
                if ( this.pageLoader.total <= this.pageLoader.current )
                {
                    this.pageLoader.onComplete();
                }
            }
        };

        this.app.container.innerHTML = this.app.view.getHtml( template );
        gsap.registerPlugin(ScrollTrigger,Observer,ScrollToPlugin,TextPlugin,ScrollSmoother,ScrambleTextPlugin,SplitText);

        this.scrollSmooth = ScrollSmoother.create({
            smooth: 1, // how long (in seconds) it takes to "catch up" to the native scroll position
            effects: false, // looks for data-speed and data-lag attributes on elements
            smoothTouch: 0.1, // much shorter smoothing time on touch devices (default is NO smoothing on touch devices)
        });

        // Headermenu Initialisation
        this.setupHeader();

        // Telex paragraphs
        //const split = new SplitText( '.telex', { type: "words", wordsClass : 'splitWord' } );
        const telexParagraphs = gsap.utils.toArray( '.telex' );
        telexParagraphs.forEach( ( telexParagraph, i ) => {
            telexParagraph.querySelectorAll( '.splitWord' ).forEach( ( word, wordIndex ) => {
                word.style.transitionDelay = `${( wordIndex * 10 )}ms`;
            });
            gsap.from(telexParagraph, {
                scrollTrigger: {
                    start: 'top bottom',
                    end: 'bottom top',
                    trigger: telexParagraph,
                    onEnter() {
                        if ( telexParagraph.classList.contains( 'telex-in' ) )
                        {
                            return;
                        }
                        telexParagraph.classList.add( 'telex-in' );
                    }
                }
            });
        });

        // Headlines scramble typer
        const subHeadlines = gsap.utils.toArray( '.headline-scramble' );
        subHeadlines.forEach( ( subHeadline ) => {
            const endHeight = subHeadline.offsetHeight || subHeadline.clientHeight;
            subHeadline.setAttribute( 'height', endHeight );
            subHeadline.innerText = subHeadline.getAttribute( 'data-text' );
            gsap.from(subHeadline, {
                scrollTrigger: {
                    start: 'top bottom',
                    end: 'bottom top',
                    trigger: subHeadline,
                    onEnter() {
                        if ( subHeadline.innerText.length <= 1 ) {
                            gsap.to( subHeadline, {
                                duration : subHeadline.getAttribute( 'data-text' ).length * 0.05,
                                text : subHeadline.getAttribute( 'data-text' )
                                //scrambleText: subHeadline.getAttribute( 'data-text' )
                            });
                        }
                    }
                }
            });
        });

        setTimeout( () =>
        {
            const shContainer = gsap.utils.toArray( '.service-box-header' );
            shContainer.forEach( ( sh ) =>
            {
                sh.querySelector( '.headline-scramble' ).innerHTML = '&nbsp;';
            });

            const refContainer = gsap.utils.toArray( '.ref-container-header' );
            let refClientHeight = 0;
            refContainer.forEach( ( rc ) =>
            {
                if ( rc.clientHeight > refClientHeight )
                {
                    refClientHeight = rc.clientHeight;
                }
                rc.querySelector( '.headline-scramble' ).innerHTML = '&nbsp;';
            });
            refContainer.forEach( ( rc ) =>
            {
                rc.style.height = refClientHeight + 'px';
            });
        }, 1500 );

        // Reveal divs
        const reveals = gsap.utils.toArray( '.reveal-container' );
        reveals.forEach( ( revealContainer, index ) => {
            const delay = revealContainer.hasAttribute( 'data-delay' ) ? parseFloat( revealContainer.getAttribute( 'data-delay' ) ) : 0;
            gsap.from(revealContainer, {
                scrollTrigger: {
                    start: 'top bottom',
                    end: 'bottom top',
                    trigger: revealContainer,
                    onEnter() {
                        if ( revealContainer.classList.contains( 'animate-in' ) )
                        {
                            return;
                        }
                        setTimeout( () => {
                            revealContainer.classList.add('animate-in');
                        }, 1000 * delay );

                    }
                }
            });
        });

        // Scroll Sections
        const scrollSections = gsap.utils.toArray( "section" );
        this.mainMenu = document.querySelector( '[data-id="main-menu"]' );
        scrollSections.forEach( ( section, index ) =>
        {
            ScrollTrigger.create(
                {
                    trigger: section,
                    start: "+200px",
                    end: "-200px",
                    onEnter: () => { this.enteringSection( section ); },
                    onEnterBack: () => { this.enteringSection( section ); },
                    onLeave: () => { this.leavingSection( section ); },
                    onLeaveBack: () => { this.leavingSection( section ); },
                }
            );
        });

        this.pageLoader.init();

        if ( this.app.router.currentRoute && this.app.router.currentRoute.length > 3 )
        {
            this.switchInnerState( this.app.router.currentRoute );
        }

        const sm = new SuperMarquee(
            document.getElementById( 'tech-scroller' ),
            {
                license : 'our-very-own',
                speed : 'slow',
                content : `<img src="/assets/images/techicons/HTML5 CSS3@2x-1.png" /> <img src="/assets/images/techicons/Infront JS@2x-1.png" /> <img src="/assets/images/techicons/Three JS@2x-1.png" />  <img src="/assets/images/techicons/WebGL WebGPU@2x-1.png" /> <img src="/assets/images/techicons/PIXI JS@2x-1.png" /> <img src="/assets/images/techicons/nodejs@2x-1.png" /> <img src="/assets/images/techicons/Open AI@2x-1.png" /> <img src="/assets/images/techicons/Express@2x-1.png" /> <img src="/assets/images/techicons/Google Cloud@2x-1.png" /> <img src="/assets/images/techicons/MongoDB@2x-1.png" /> <img src="/assets/images/techicons/SQL@2x-1.png" />\``
            }
        );

        this.initContactForm();
        this.heroWebGl = new HeroWebGl( this.pageLoader );
        this.aboutUsWebGl = new AboutUsWebGl( this.pageLoader );
        this.mainMenuWebGl = new MainMenuWebGl( this.pageLoader );
    }

    initContactForm()
    {
        document.querySelector( '[data-id="btn-submit-contact-form"]' ).addEventListener( 'click', async( e ) =>
        {
            const button = e.target;
            const form = document.querySelector( '[data-id="contact-form"]');
            if ( true === form.checkValidity() )
            {
                e.preventDefault();
                button.setAttribute( 'disabled', true );
                // Note, we need to get all data before we disable them
                const formJson = Helper.serializeForm( form );
                form.querySelectorAll( 'input,textarea' ).forEach( ( htmlElement ) =>
                {
                    if ( htmlElement.type == 'INPUT' )
                    {
                        htmlElement.setAttribute( 'readonly', true );
                    }
                    else
                    {
                        htmlElement.setAttribute( 'disabled', true );
                    }
                });

                const response = await fetch("/sendContactForm.php", {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify( formJson )
                });

                const result = await response.json();
                if ( !result || result.hasOwnProperty( 'result' ) || result.result !== 'success' )
                {
                    button.classList.add( 'btn-email-sent' );
                }
                else
                {
                    button.classList.add( 'btn-email-failure' );
                }
            }
            return false;
        });
    }

    switchInnerState( innerRoute )
    {
        if ( innerRoute.indexOf( 'legal' ) > -1 )
        {
            this.showOffcanvasContainer( 'legal', innerRoute );
        }
        else if ( innerRoute.indexOf( '/ref/' ) > -1 )
        {
            this.showOffcanvasContainer( 'ref', innerRoute );
        }
        else if ( innerRoute.indexOf( '/serv/' ) > -1 )
        {
            this.showOffcanvasContainer( 'serv', innerRoute );
        }
    }

    initHeroTelex()
    {
        document.querySelector( '.hero-cursor' ).classList.add( 'blinking-cursor' );
        const heroTextContainer = document.querySelector( '[data-id="hero-telex"]' );
        const heroTextContainer2 = document.querySelector( '[data-id="hero-telex-2"]' );
        const tl = gsap.timeline( { delay: 0.5, ease: "none" } );

        if ( document.scrollTop > 0 )
        {
            document.querySelector( 'body' ).style.overflow = "visible";
        }

        tl.to( heroTextContainer,
            {
                duration: 1,
                ease: "none",
                text:"VON DER VISION",
                onComplete: () => {
                    heroTextContainer.innerHTML += '<br />';
                    document.querySelector( 'body' ).style.overflow = "visible";
                    document.getElementById( 'mobile-header' ).classList.add( 'fade-in' );
                    setTimeout( () =>
                    {
                        this.heroWebGl.run();
                    }, 250 )
                }
            }
        );
        tl.to(
            heroTextContainer2,
            {
                delay: 1,
                duration: 1.0,
                ease: "none",
                onComplete: () => {
                    heroTextContainer2.innerHTML += '<br />';
                },
                text:"ZUR INNOVATION.",
            }
        );
    }

    showOffcanvasContainer( contentType, contentId = null )
    {
        const offcanvasFullContainer = document.querySelector( '#offcanvas-full-container' );
        const offcanvasContent = offcanvasFullContainer.querySelector( '#offcanvas-content' );

        if ( contentId === "/" )
        {
            const mobileHeader = document.getElementById( 'mobile-header' );
            const mobileBurgerMenu = mobileHeader.querySelector( '.burger-menu' );
            mobileBurgerMenu.click();
        }
        else
        {

            if ( 'menu' !== contentType )
            {
                this.app.addCustomEventListener(
                    IF.CustomEvents.TYPE.POPSTATE,
                    this._myPopEventHandler = ( e ) => {
                        this.hideOffcanvasContainer();
                    }
                );
            }

            offcanvasFullContainer.style.display = "flex";
            offcanvasContent.classList.add( 'show-in' );

            setTimeout( () =>
            {
                const mobileHeader = document.getElementById( 'mobile-header' );
                const mobileBurgerMenu = mobileHeader.querySelector( '.burger-menu' );

                switch( contentType )
                {
                    case 'menu':
                        offcanvasContent.querySelector( 'div' ).innerHTML = templateOffcanvasMenu;
                        this.mainMenuWebGl.show();
                        const menuItems = offcanvasContent.querySelectorAll( 'li[data-href]' );
                        menuItems.forEach( ( item, idx ) =>
                        {
                            item.addEventListener( 'click', () =>
                            {
                                mobileBurgerMenu.click();
                                this.scrollSmooth.scrollTo( '#' + item.getAttribute( 'data-href' ), true );
                                //this.app.lyte.trackEvent( 'mainMenu', item.getAttribute( 'data-href' ) );
                            });
                            const textItem = item.querySelector('span[data-text]' );
                            textItem.innerHTML = '';
                            gsap.to(
                                textItem,
                                {
                                    duration : 0.25,
                                    delay : .5 + (idx * 0.175),
                                    scrambleText : textItem.getAttribute( 'data-text' )
                                }
                            );
                        });
                        this.app.lyte.trackPageView( 'Main Menu' );
                    break;

                    case 'ref':
                        this.app.container.classList.add( 'ref-detail' );
                        mobileBurgerMenu.classList.remove( 'burger-menu--closed' );
                        mobileBurgerMenu.classList.add( 'burger-menu--opened' );
                        let currentRefHtml = '';
                        switch ( contentId )
                        {
                            case '/ref/actiboard':
                                currentRefHtml = refTemplateActiboard;
                                break;
                            case '/ref/highperformance-components':
                                currentRefHtml = refTemplateHighPerformanceComponents;
                                break;
                            case '/ref/supermarquee':
                                currentRefHtml = refTemplateSuperMarquee;
                                break;
                            case '/ref/jubeldeal':
                                currentRefHtml = refTemplateJubelDeal;
                                break;
                            case '/ref/enportal-connect':
                                currentRefHtml = refTemplateEnportalConnect;
                                break;
                            case '/ref/enportal-compete':
                                currentRefHtml = refTemplateEnportalCompete;
                                break;
                            case '/ref/diversitize-app':
                                currentRefHtml = refTemplateDiversitizeApp;
                                break;
                            case '/ref/youtine':
                                currentRefHtml = refTemplateYoutine;
                                break;
                        }

                        offcanvasContent.querySelector( 'div' ).innerHTML = this.app.view.getHtml( refTemplateContainer, { refHtml : currentRefHtml } );

                        offcanvasContent.querySelector( '[data-id="ref-button-contact"]' ).addEventListener( 'click', () =>
                        {
                            mobileBurgerMenu.click();
                            this.scrollSmooth.scrollTo( '#contact', true );
                        });

                        this.app.lyte.trackPageView( 'Reference: ' + contentId.split( "/" ).pop() );
                    break;

                    case 'serv':
                        this.app.container.classList.add( 'serv-detail' );
                        mobileBurgerMenu.classList.remove( 'burger-menu--closed' );
                        mobileBurgerMenu.classList.add( 'burger-menu--opened' );
                        let currentServHtml = '';
                        switch ( contentId )
                        {
                            case '/serv/digital-business-saas':
                                currentServHtml = servDigitalBusiness;
                            break;

                            case '/serv/games-creative-3d':
                                currentServHtml = servGamesCreative;
                            break;

                            case '/serv/plugin-api-development':
                                currentServHtml = servPluginApiDevelopment;
                            break;

                            case '/serv/tools-automation':
                                currentServHtml = servToolsAutomation;
                            break;
                        }

                        offcanvasContent.querySelector( 'div' ).innerHTML = this.app.view.getHtml( servTemplateContainer, { servHtml : currentServHtml } );

                        offcanvasContent.querySelector( '[data-id="ref-button-contact"]' ).addEventListener( 'click', () =>
                        {
                            mobileBurgerMenu.click();
                            this.scrollSmooth.scrollTo( '#contact', true );
                        });

                        this.app.lyte.trackPageView( 'Services: ' + contentId.split( "/" ).pop() );
                break;



                    case 'legal':
                        this.app.container.classList.add( 'ref-detail' );
                        mobileBurgerMenu.classList.remove( 'burger-menu--closed' );
                        mobileBurgerMenu.classList.add( 'burger-menu--opened' );
                        switch ( contentId )
                        {
                            case '/legal/impressum':
                                offcanvasContent.querySelector( 'div' ).innerHTML = legalTemplateImprint;
                                break;
                            case '/legal/agb':
                                offcanvasContent.querySelector( 'div' ).innerHTML = legalTemplateTerms;
                                break;
                            case '/legal/disclaimer':
                                offcanvasContent.querySelector( 'div' ).innerHTML = legalTemplateDisclaimer;
                                break;
                            case '/legal/datenschutz':
                                offcanvasContent.querySelector( 'div' ).innerHTML = legalTemplateDataPrivacy;
                                break;
                        }
                        break;
                }
            }, 100 );
            setTimeout( () =>
            {
                const mobileHeader = document.getElementById( 'mobile-header' );
                const mobileBurgerMenu = mobileHeader.querySelector( '.burger-menu' );
                mobileBurgerMenu.style.pointerEvents = 'initial';
            }, 1000 );

        }
    }

    hideOffcanvasContainer()
    {
        let contentType = 'menu';
        if ( this.app.router.currentRoute.indexOf( '/legal/' ) > -1 )
        {
            contentType = 'legal';
        }
        else if ( this.app.router.currentRoute.indexOf( '/ref/' ) > -1 )
        {
            contentType = 'ref';
        }
        else if ( this.app.router.currentRoute.indexOf( '/serv/' ) > -1 )
        {
            contentType = 'serv';
        }

        if ( this._myPopEventHandler )
        {
            const mobileHeader = document.getElementById( 'mobile-header' );
            const mobileBurgerMenu = mobileHeader.querySelector( '.burger-menu' );
            mobileBurgerMenu.classList.remove( 'burger-menu--opened' );
            mobileBurgerMenu.classList.add( 'burger-menu--closed' );

            this.app.removeCustomEventListener(
                IF.CustomEvents.TYPE.POPSTATE,
                this._myPopEventHandler
            );
            this._myPopEventHandler = null;
        }

        document.querySelector( '#offcanvas-content' ).classList.add( 'animation-rtl' );
        document.querySelector( '#offcanvas-content' ).classList.remove( 'animation-ltr' );
        this.app.router.setUrl( '/' );
        this.app.router.currentRoute = '/';
        setTimeout( () => {
            document.querySelector( '#offcanvas-content' ).querySelector( 'div' ).innerHTML = '';
            document.querySelector( '#offcanvas-content' ).querySelector( 'div' ).style.backgroundColor = 'transparent';
            this.app.container.classList.remove( 'ref-detail' );
            this.app.container.classList.remove( 'serv-detail' );
            if ( 'menu' === contentType )
            {
                this.mainMenuWebGl.hide();
            }
        }, 600 );
        setTimeout( () =>
        {
            const mobileHeader = document.getElementById( 'mobile-header' );
            const mobileBurgerMenu = mobileHeader.querySelector( '.burger-menu' );
            document.querySelector( '#offcanvas-content' ).classList.remove( 'show-in' );
            document.querySelector( '#offcanvas-content' ).classList.remove( 'animation-rtl' );
            document.querySelector( '#offcanvas-content' ).classList.add( 'animation-ltr' );
            document.querySelector( '#offcanvas-content' ).querySelector( 'div' ).style.backgroundColor = '#1c1c1c';
            document.querySelector( '#offcanvas-full-container' ).style.display = "none";
            mobileBurgerMenu.style.pointerEvents = 'initial';
        }, 1200 );
    }

    setupHeader()
    {

        // Mobile Header
        const mobileHeader = document.getElementById( 'mobile-header' );
        const mobileBurgerMenu = mobileHeader.querySelector( '.burger-menu' );

        mobileBurgerMenu.addEventListener( 'click', () =>
        {
            mobileBurgerMenu.style.pointerEvents = 'none';
            if ( true === mobileBurgerMenu.classList.contains( 'burger-menu--opened' ) )
            {
                mobileBurgerMenu.classList.remove( 'burger-menu--opened' );
                mobileBurgerMenu.classList.add( 'burger-menu--closed' );
                this.hideOffcanvasContainer();
            }
            else
            {
                mobileBurgerMenu.classList.remove( 'burger-menu--closed' );
                mobileBurgerMenu.classList.add( 'burger-menu--opened' );
                this.showOffcanvasContainer( 'menu' );
            }
        });
    }

    enteringSection( section )
    {
        const nextSection = section.nextElementSibling;
        if ( nextSection && false === nextSection.hasAttribute( 'data-processed' ) )
        {
            nextSection.setAttribute( 'data-processed', true );
        }
    }

    leavingSection( section )
    {
        // const previousSection = section.previousElementSibling;
        // if ( previousSection && false === previousSection.hasAttribute( 'data-processed' ) )
        // {
        // }
    }
}
